import React, { useState, useEffect } from 'react';
import {
  Modal, Box, Typography, TextField, Button, Stack,
  MenuItem, Divider, Alert, CircularProgress
} from '@mui/material';
import PropTypes from 'prop-types';
import { updateCategory } from '../../services/categoryServices';

const DettaglioCategoriaModal = ({ open, handleClose, categoria, onUpdate, parentCategories }) => {
  const [formData, setFormData] = useState({
    nome: '',
    parentId: '',
    prezzoPpc: '',
    inizioValidita: '',
    fineValidita: '',
    isDeleted: 0
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (categoria) {
      setFormData({
        nome: categoria.nome || '',
        parentId: categoria.parentId || '',
        prezzoPpc: categoria.prezzoPpc || '',
        inizioValidita: categoria.inizioValidita || new Date().toISOString().split('T')[0],
        fineValidita: categoria.fineValidita || '',
        isDeleted: categoria.isDeleted || 0
      });
    }
  }, [categoria]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'nome') {
      const filteredValue = value.replaceAll(/[^a-zA-ZàèéìòùÀÈÉÌÒÙ\s']/g, '');
      setFormData({ ...formData, [name]: filteredValue });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSave = async () => {
    if (!formData.nome.trim()) {
      setError("Il nome della categoria non può essere vuoto.");
      return;
    }
    const nameRegex = /^[a-zA-ZàèéìòùÀÈÉÌÒÙ\s']+$/;
    if (!nameRegex.test(formData.nome.trim())) {
      setError("Il nome della categoria contiene caratteri non validi (ammessi solo lettere, spazi e apostrofi).");
      return;
    }

    if (formData.prezzoPpc && Number.parseFloat(formData.prezzoPpc) < 0) {
      setError("Il costo PPC non può essere negativo.");
      return;
    }

    setLoading(true);
    setError('');
    try {
      const request = {
        categoryId: categoria.categoryId,
        nome: formData.nome,
        parentId: formData.parentId || null,
        prezzoPpc: formData.prezzoPpc ? Number.parseFloat(formData.prezzoPpc) : null,
        inizioValidita: formData.inizioValidita || null,
        fineValidita: formData.fineValidita || null,
        isDeleted: Number.parseInt(formData.isDeleted, 10)
      };
      await updateCategory(request);
      onUpdate();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={{
        position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        width: 500, bgcolor: 'background.paper', boxShadow: 24, p: 4, borderRadius: 3
      }}>
        <Typography variant="h6" fontWeight="bold" color="#0b3d87" mb={2}>
          Dettaglio Categoria (Editing Protetto)
        </Typography>
        <Divider sx={{ mb: 3 }} />

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Stack spacing={3}>
          <TextField
            label="ID Univoco (Sola Lettura)"
            value={categoria?.categoryId || ''}
            fullWidth
            disabled
            variant="filled"
          />
          <TextField
            label="Nome Categoria"
            name="nome"
            value={formData.nome}
            onChange={handleChange}
            fullWidth
          />
          <TextField
            select
            label="Categoria Padre"
            name="parentId"
            value={formData.parentId}
            onChange={handleChange}
            fullWidth
          >
            <MenuItem value="">Nessuna (Macro-categoria)</MenuItem>
            {Array.isArray(parentCategories) && parentCategories.map(cat => (
              <MenuItem key={cat.categoryId} value={cat.categoryId} disabled={cat.categoryId === categoria.categoryId}>
                {cat.nome}
              </MenuItem>
            ))}
          </TextField>
          
          <Stack direction="row" spacing={2}>
            <TextField
              label="Costo PPC (€)"
              name="prezzoPpc"
              type="number"
              value={formData.prezzoPpc}
              onChange={handleChange}
              fullWidth
              slotProps={{ htmlInput: { step: "0.01" } }}
            />
          </Stack>

          <Stack direction="row" spacing={2}>
            <TextField
              label="Inizio Validità"
              name="inizioValidita"
              type="date"
              fullWidth
              value={formData.inizioValidita}
              onChange={handleChange}
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <TextField
              label="Fine Validità"
              name="fineValidita"
              type="date"
              fullWidth
              value={formData.fineValidita}
              onChange={handleChange}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Stack>

          <TextField
            select
            label="Stato Operativo"
            name="isDeleted"
            value={formData.isDeleted}
            onChange={handleChange}
            fullWidth
            helperText="Attenzione: Disattivare la categoria escluderà i prodotti dal comparatore."
          >
            <MenuItem value={0}>Attiva</MenuItem>
            <MenuItem value={1}>Inattiva/Eliminata</MenuItem>
          </TextField>
        </Stack>

        <Stack direction="row" justifyContent="flex-end" spacing={2} mt={4}>
          <Button onClick={handleClose} color="inherit">Chiudi</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={loading}
            sx={{ backgroundColor: '#0b3d87', fontWeight: 'bold' }}
          >
            {loading ? <CircularProgress size={24} /> : "Salva Modifiche"}
          </Button>
        </Stack>
      </Box>
    </Modal>
  );
};

DettaglioCategoriaModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  categoria: PropTypes.shape({
    categoryId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    nome: PropTypes.string,
    parentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    prezzoPpc: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    inizioValidita: PropTypes.string,
    fineValidita: PropTypes.string,
    isDeleted: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  }),
  onUpdate: PropTypes.func.isRequired,
  parentCategories: PropTypes.arrayOf(PropTypes.shape({
    categoryId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    nome: PropTypes.string
  }))
};

export default DettaglioCategoriaModal;
 